import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GlobalStateContext } from '../context/GlobalStateContext';
import 'tailwindcss/tailwind.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
// Mark without its disc: the badge is teal and would vanish on a teal bar,
// which is what the white circle around it used to be compensating for.
import logo from '../assests/logo-mark.svg';

/**
 * Admin navigation, grouped by the job being done rather than listed flat.
 *
 * Eleven links in one row overflowed the bar and gave no sense of where you
 * were; these four groups hold every destination, each reachable in at most two
 * clicks. A group with `to` is itself a link — «Ученики» goes straight to the
 * student list on click and reveals the rest on hover, so the page used most
 * does not cost an extra click.
 *
 * «Назначение TG домашек» is here deliberately: it existed only in the mobile
 * menu, so on desktop the page was unreachable.
 */
export const ADMIN_NAV = [
  {
    label: 'Ученики',
    to: '/students',
    items: [
      { label: 'Управление студентами', to: '/students', icon: 'fa-users' },
      { label: 'Назначение TG домашек', to: '/admin/telegram-homework-assignment', icon: 'fa-clipboard-check' },
      { label: 'Активность в курсе', to: '/read-activity', icon: 'fa-chart-bar' },
    ],
  },
  {
    label: 'Урок',
    to: '/online-session',
    items: [{ label: 'Онлайн сессия', to: '/online-session', icon: 'fa-graduation-cap' }],
  },
  {
    label: 'Деньги',
    items: [
      { label: 'Бюджет', to: '/admin', icon: 'fa-wallet' },
      { label: 'Месячный отчёт', to: '/monthly-report', icon: 'fa-chart-line' },
      { divider: true },
      { label: 'Счёт', to: '/invoice-generator', icon: 'fa-file-invoice' },
      { label: 'Условия занятий', to: '/usloviya', icon: 'fa-file-contract' },
      { label: 'Прайс', to: '/prices', icon: 'fa-tags' },
    ],
  },
  {
    label: 'Материалы',
    items: [
      { label: 'Библиотека', to: '/library', icon: 'fa-book' },
      { label: 'Чтение', to: '/read', icon: 'fa-book-open' },
      { label: '80 шпаргалок', to: '/sheets/full', icon: 'fa-sticky-note' },
      { label: 'Музыкальные упражнения', to: '/music', icon: 'fa-music' },
    ],
  },
];

/** Inline so the bar never depends on the icon font having loaded. */
const Chevron = ({ open }) => (
  <svg
    viewBox="0 0 12 12"
    aria-hidden="true"
    className={`h-2.5 w-2.5 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`}
  >
    <path d="M2 4.5 6 8.5 10 4.5" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const isPathActive = (pathname, to) =>
  to === '/' ? pathname === '/' : pathname.toLowerCase().startsWith(to.toLowerCase());

/** One top-level group: a link or button on the bar, its pages in a panel. */
export const NavGroup = ({ group, pathname, openLabel, setOpenLabel }) => {
  const open = openLabel === group.label;
  const active = group.items.some((i) => i.to && isPathActive(pathname, i.to));
  const close = () => setOpenLabel(null);

  // `after:content-['']` is required — a pseudo-element without content has no
  // box, so the underline silently never drew.
  const trigger = `relative px-3 py-2 font-medium transition after:content-[''] after:absolute
    after:left-3 after:right-3 after:bottom-0 after:h-[3px] after:rounded-full after:transition
    ${active ? 'text-white after:bg-brand-300' : 'text-white/80 hover:text-white after:bg-transparent'}`;

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpenLabel(group.label)}
      onMouseLeave={close}
    >
      {group.to ? (
        <Link to={group.to} className={`${trigger} inline-flex items-center gap-1`} onClick={close}>
          {group.label}
          {group.items.length > 1 && <Chevron />}
        </Link>
      ) : (
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => setOpenLabel(open ? null : group.label)}
          className={`${trigger} inline-flex items-center gap-1`}
        >
          {group.label}
          <Chevron open={open} />
        </button>
      )}

      {open && group.items.length > 1 && (
        <div className="absolute left-0 top-full pt-2 z-50">
          <div className="min-w-[248px] rounded-xl bg-white py-2 shadow-xl ring-1 ring-black/5">
            {group.items.map((item, i) =>
              item.divider ? (
                <div key={`d${i}`} className="my-2 border-t border-slate-100" />
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={close}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition ${
                    isPathActive(pathname, item.to)
                      ? 'bg-brand-50 font-semibold text-brand-700'
                      : 'text-brand-ink hover:bg-slate-50'
                  }`}
                >
                  <i className={`fas ${item.icon} w-4 text-center text-brand-500`} />
                  {item.label}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = ({ scrollToSection, activeSection, isDashboard }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthenticatedStudent } = useContext(GlobalStateContext);
  const menuRef = useRef(null);
  const [openLabel, setOpenLabel] = useState(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const isLoginRoute = location.pathname === '/login';
  const isHiddenNavbarRoute = isDashboardRoute || isLoginRoute;

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('rememberedUser');
    localStorage.removeItem('adminToken');
    sessionStorage.setItem('justLoggedOut', 'true');
    setAuthenticatedStudent(null);
    navigate('/login', { replace: true });
  };

  useEffect(() => setIsOpen(false), [location]);

  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  // Close mobile menu on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const scrollToHero = () => {
    const hero = document.getElementById('hero');
    if (hero) {
      hero.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed top-0 left-0 w-full z-50 px-4 py-4 md:px-6 lg:px-8 bg-gradient-to-r from-brand-600 to-brand-700 shadow font-sans">
      <div className="container mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3">
        <div className="flex items-center space-x-4">
  <div 
    onClick={scrollToHero} 
    className="h-12 w-12 flex items-center justify-center cursor-pointer transition-transform hover:scale-110" 
    aria-label="Home"
  >
    <img 
      src={logo} 
      alt="RV Logo" 
      className="h-11 w-11 object-contain" 
      loading="eager"
    />
  </div>
  {/* The name, set in the same Playfair the счёт, условия and прайс are set in,
      so the bar and the documents read as one thing. Hidden on the narrowest
      screens, where the mark alone carries it. */}
  <span
    onClick={scrollToHero}
    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
    className="hidden cursor-pointer whitespace-nowrap text-xl font-semibold tracking-tight text-white transition-colors hover:text-brand-200 sm:inline"
  >
    Роман Волконидов
  </span>
</div>
        </Link>
        
        {/* Dashboard section buttons (centered, only on dashboard) */}
        {isDashboard && (
          <div className="hidden md:flex gap-1 mx-4 overflow-x-auto">
            <Link 
              to="/" 
              className="px-3 py-2 text-white hover:text-brand-200 transition font-medium flex items-center gap-1 mx-1"
              style={{ borderRadius: '0.5rem' }}
            >
              <i className="fas fa-home text-lg"></i>
              Главная страница
            </Link>
            {[ 
              { label: 'Профиль', id: 'profile', icon: 'fa-user' },
              { label: 'Задания', id: 'table', icon: 'fa-table' },
              { label: 'Прогресс', id: 'progress', icon: 'fa-chart-bar' },
              { label: 'Расписание', id: 'schedule', icon: 'fa-calendar-alt' },
              { label: 'Финансы', id: 'financial', icon: 'fa-dollar-sign' }
            ].map(section => (
              <a
                key={section.id}
                href="#"
                onClick={e => {
                  e.preventDefault();
                  scrollToSection && scrollToSection(section.id);
                }}
                className={`px-3 py-2 text-white hover:text-brand-200 transition font-medium flex items-center gap-1 mx-1 ${activeSection === section.id ? 'font-bold underline underline-offset-4' : ''}`}
                title={section.label}
                style={{ borderRadius: '0.5rem' }}
              >
                <i className={`fas ${section.icon} text-lg`}></i>
                {section.label}
              </a>
            ))}
          </div>
        )}
        
        {/* Login page - show only Home link */}
        {isLoginRoute && (
          <div className="hidden md:flex gap-1 mx-4 overflow-x-auto">
            <Link 
              to="/" 
              className="px-3 py-2 text-white hover:text-brand-200 transition font-medium flex items-center gap-1 mx-1"
              style={{ borderRadius: '0.5rem' }}
            >
              <i className="fas fa-home text-lg"></i>
              Главная страница
            </Link>
          </div>
        )}
        
        {/* Right-side menu */}
        <div className="flex items-center space-x-4">
          {isDashboard && (
            <button
              onClick={handleLogout}
              className="hidden md:block bg-white hover:bg-gray-100 text-brand-600 px-5 py-2 ml-2 rounded-full transition duration-300 shadow-md font-medium"
            >
              Выход
            </button>
          )}
            {!isHiddenNavbarRoute && (
            <div className="lg:hidden">
              {isOpen ? (
                <div onClick={(e) => {
                  e.stopPropagation(); // Stop event propagation
                  closeMenu();
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              ) : (
                <div onClick={toggleMenu}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </div>
              )}
            </div>
          )}
            {isDashboard && (
            <div className="md:hidden">
              {isOpen ? (
                <div onClick={(e) => {
                  e.stopPropagation(); // Stop event propagation
                  closeMenu();
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              ) : (
                <div onClick={toggleMenu}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Desktop navigation. Four groups instead of eleven flat links — see
            ADMIN_NAV for why, and for the page that was desktop-unreachable. */}
        <div className="hidden lg:flex items-center gap-1">
          {isAuthenticated && !isHiddenNavbarRoute && (
            <>
              {isAdmin ? (
                ADMIN_NAV.map((group) => (
                  <NavGroup
                    key={group.label}
                    group={group}
                    pathname={location.pathname}
                    openLabel={openLabel}
                    setOpenLabel={setOpenLabel}
                  />
                ))
              ) : (
                <>
                  <Link to="/" className="px-3 py-2 text-white/90 hover:text-white transition font-medium">
                    Главная
                  </Link>
                  <Link to="/read" className="px-3 py-2 text-white/90 hover:text-white transition font-medium">
                    <i className="fas fa-book-open mr-1" /> Чтение
                  </Link>
                </>
              )}

              {isAuthenticated && !isLoginRoute && (
                <button
                  onClick={handleLogout}
                  className="ml-3 rounded-full bg-white/10 px-4 py-2 font-medium text-white ring-1 ring-white/25 transition hover:bg-white/20"
                >
                  Выход
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div ref={menuRef} className="bg-gradient-to-r from-brand-600 to-brand-700 px-4 pt-4 pb-2 rounded-b-xl shadow-lg animate-slide-down-fade z-[999] fixed top-16 left-0 w-full">
          {isAuthenticated && !isHiddenNavbarRoute && (
            <>
              <Link
                to="/"
                className="block rounded px-3 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                onClick={() => setIsOpen(false)}
              >
                Главная
              </Link>

              {isAdmin ? (
                // Same groups as the desktop bar, opened out: on a phone there is
                // room to show every page under its heading, so nothing hides.
                ADMIN_NAV.map((group) => (
                  <div key={group.label} className="mt-3">
                    <div className="px-3 pb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-300">
                      {group.label}
                    </div>
                    {group.items
                      .filter((i) => !i.divider)
                      .map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 rounded px-3 py-2 text-sm font-medium transition ${
                            isPathActive(location.pathname, item.to)
                              ? 'bg-white/15 text-white'
                              : 'text-white/90 hover:bg-white/10'
                          }`}
                        >
                          <i className={`fas ${item.icon} w-4 text-center text-brand-300`} />
                          {item.label}
                        </Link>
                      ))}
                  </div>
                ))
              ) : (
                <Link
                  to="/read"
                  className="flex items-center gap-2 rounded px-3 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                  onClick={() => setIsOpen(false)}
                >
                  <i className="fas fa-book-open" /> Чтение
                </Link>
              )}

              <button 
                onClick={() => { setIsOpen(false); handleLogout(); }}
                className="w-full mt-2 bg-white hover:bg-gray-100 text-brand-600 px-4 py-2 rounded-full transition duration-300 shadow-md font-medium text-sm"
              >
                Выход
              </button>
            </>
          )}
          
          {/* Login page - show home link in mobile menu */}
          {isLoginRoute && (
            <div className="py-2">              <Link
                to="/"
                className="px-3 py-2 text-white rounded hover:bg-brand-700 transition font-medium text-sm flex items-center gap-2 mb-1"
                onClick={() => setIsOpen(false)}
              >
                <i className="fas fa-home text-lg"></i>
                Главная страница
              </Link>
            </div>
          )}
          
          {/* Dashboard nav items for mobile */}
          {isDashboard && (
            <div className="mt-3 border-t border-brand-400 pt-3">
              <h3 className="text-white text-xs font-semibold mb-2">Разделы:</h3>              <Link
                to="/"
                className="px-3 py-2 text-white rounded bg-brand-700 transition font-medium text-sm flex items-center gap-2 mb-1"
                onClick={() => setIsOpen(false)}
              >
                <i className="fas fa-home text-lg"></i>
                Главная страница
              </Link>
              {[ 
                { label: 'Профиль', id: 'profile', icon: 'fa-user' },
                { label: 'Задания', id: 'table', icon: 'fa-table' },
                { label: 'Прогресс', id: 'progress', icon: 'fa-chart-bar' },
                { label: 'Расписание', id: 'schedule', icon: 'fa-calendar-alt' },
                { label: 'Финансы', id: 'financial', icon: 'fa-dollar-sign' }
              ].map(section => (
                <a
                  key={section.id}
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    scrollToSection && scrollToSection(section.id);
                    setIsOpen(false);
                  }}
                  className={`px-3 py-2 text-white rounded hover:bg-brand-700 transition font-medium text-sm flex items-center gap-2 mb-1 ${activeSection === section.id ? 'bg-brand-700' : ''}`}
                >
                  <i className={`fas ${section.icon} text-lg`}></i>
                  {section.label}
                </a>
              ))}
              {/* Always show logout button in dashboard mobile menu */}
              <button 
                onClick={() => { setIsOpen(false); handleLogout(); }}
                className="w-full mt-4 bg-white hover:bg-gray-100 text-brand-600 px-4 py-2 rounded-full transition duration-300 shadow-md font-medium text-sm"
              >
                Выход
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;